import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { SidebarService } from '../main/common/menu/service/sidebar.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { MOI } from '../shared/enums/moi.enum';
import { TranslateService } from '@ngx-translate/core';
import { SystemMessagesEnum } from '../shared/enums/system-messages.enum';
import { BiggerPictureMessagesService } from '../shared/services/bigger-picture-messages.service';
import { AuthService } from './auth.service';
import { StorageConst } from '../shared/consts/storage-consts';
import { RoutesUrl } from '../shared/consts/routes';

@Injectable()
export class RouteGuard implements CanActivateChild, CanActivate {

  private routes: string[] = [];
  private emptyMenuNotified = false;

  // Here can supply more routes that will not be presented in menuItems
  private whitelist = [
    '/home',
    '/dashboard',
    '/sales-monitor',
    '/invcount',
    '/invcounter',
    '/invadj',
    '/monitoring-dashboard',
  ];

  constructor(
    private router: Router,
    private sidebarService: SidebarService,
    private translate: TranslateService,
    private biggerPictureMessagesService: BiggerPictureMessagesService,
    private authService: AuthService
  ) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.verify(state);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.verify(state);
  }

  /**
   * Load menu items and check if some of them match current route
   * fetchMenuTreeItems() is a singleton so only once will fetch the results from api
   * @param state
   * @private
   */
  private verify(state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const currentUser: any = JSON.parse(sessionStorage.getItem('USER'));
    return this.sidebarService.fetchMenuTreeItems(currentUser.menuId, currentUser.systemMenuId).pipe(
      map((items) => {
        const menuItems: any[] = Array.isArray(items) ? items : [];

        // The menu API returned no items for this user (e.g. their user-class has no menu
        // permissions for the current menuId/systemMenuId). A monitor/kiosk auto-login
        // (x + y + accessCode) legitimately has no menu and is routed to the whitelisted
        // /sales-monitor by AuthGuard, so it must NOT be signed out. For any other session an
        // empty menu means there is no usable screen, so tell the user and sign them out instead
        // of leaving them on a blank/stuck loader.
        if (!menuItems.length && !this.isAutoLoginSession()) {
          if (!this.emptyMenuNotified) {
            this.emptyMenuNotified = true;
            Swal.fire({
              title: this.translate.instant(this.tranKey(MOI.SYSTEM, SystemMessagesEnum.NoMenuItems)),
              icon: 'warning',
              showCloseButton: true
            }).then(() => this.signOutToLogin());
          }
          return false;
        }

        this.routes = [
          ...this.whitelist.map(item => this.normalizeURL(item)),
          ...this.getRoutes(menuItems)
        ];

        if (!this.routes.some(route => this.normalizeURL(state.url).indexOf(route) > -1)) {
          console.warn(`ROUTE: (${state.url}) is not allowed!`);
          // return this.router.createUrlTree(['404']);
          Swal.fire({
            title: this.translate.instant(this.tranKey(MOI.SYSTEM, SystemMessagesEnum.RouteNotAllowed), {default: 'Route not allowed!'}),
            icon: 'error',
            showCloseButton: true
          });
          return this.router.createUrlTree(['/']);
        }
        return true;
      })
    );
  }

  /**
   * A monitor/kiosk auto-login session carries x + y + accessCode in sessionStorage (set by
   * AuthGuard). Such sessions intentionally have no menu and are redirected to the whitelisted
   * /sales-monitor, so they must be exempt from the "no menu -> sign out" handling.
   * @private
   */
  private isAutoLoginSession(): boolean {
    return !!(sessionStorage.getItem(StorageConst.X)
      && sessionStorage.getItem(StorageConst.Y)
      && sessionStorage.getItem(StorageConst.ACCESS_CODE));
  }

  /**
   * Sign the user out and send them back to the login page. Used when the user has no usable
   * menu, so there is nothing to keep them logged in for. Mirrors the header logout flow and
   * always returns to login even if the logout API call fails.
   * @private
   */
  private signOutToLogin(): void {
    this.authService.logout()
      .catch(() => { /* ignore: still clear the session and return to login */ })
      .then(() => {
        this.biggerPictureMessagesService.reset();
        sessionStorage.removeItem(StorageConst.TOKEN);
        sessionStorage.removeItem(StorageConst.USER);
        const accessCode = sessionStorage.getItem(StorageConst.ACCESS_CODE);
        window.location.href = accessCode
          ? `${RoutesUrl.session.signIn}?accessCode=${accessCode}`
          : RoutesUrl.session.signIn;
      });
  }

  /**
   * Recursion to extract all routerLinks as string[]
   * @param items
   * @private
   */
  private getRoutes(items: any[]): string[] {
    return items.reduce((acc, item) => {
      if (item.routerLink) {
        acc.push(this.normalizeURL(item.routerLink));
      } else {
        acc.push(...this.getRoutes(item.items || []));
      }
      return acc;
    }, []);
  }

  /**
   * Normalizing the url removes queryParams and adds "/" as end of routing
   * @param url
   * @private
   */
  private normalizeURL(url: string): string {
    let normalizedUrl = url.split('?')[0];
    normalizedUrl = normalizedUrl.substring(-1) === '/' ? normalizedUrl : normalizedUrl + '/';
    return normalizedUrl;
  }

  tranKey(moi: MOI, msgId: number, type = 'text'): string {
    return this.biggerPictureMessagesService.tranKey(moi, msgId, type);
  }
}
